/** @format */

import {type MetricKind} from 'features/mediaplan-progress/types';

/**
 * Dynamic-mode chart heights so each compact MainChart fills its container:
 *  - a KPI chart fills its row → chart height = the row's text-block height,
 *    which grows with the number of text rows (additive shows «План на сегодня», ratio doesn't);
 *  - the Расход chart fills the whole right column → its height tracks the KPI column.
 *
 * Constants are the measured heights of the KpiCharts layout (KpiCharts/styles.ts):
 * KpiLabel 20 + gap 8 + SummaryGrid rows (~16.8px each). Keep in sync if that layout changes.
 */

// Text-block height per KPI kind: additive = 5 rows (incl. «План на сегодня»), ratio = 4 rows.
const KPI_TEXT_BLOCK_HEIGHT: Record<MetricKind, number> = {
  additive: 112,
  ratio: 96,
};

const KPI_ROW_PADDING = 32; // KpiRow padding 16px top + 16px bottom
const KPI_ROW_DIVIDER = 1; // RowDivider 1px border between rows

// The compact overview chart is a bare MainChart (no MultilineChart x-axis stack / top offset),
// so it fills exactly the height we give it. Chart height = its row content height.

/** Chart height for one KPI so it fills its row (= the row's text-block height). */
export const kpiChartHeight = (kind: MetricKind): number => KPI_TEXT_BLOCK_HEIGHT[kind];

/** Inner height of the KPI column (sum of KpiRows + inter-row dividers; the box border lives on MetricsRoot). */
export const kpiColumnHeight = (kinds: MetricKind[]): number =>
  kinds.reduce((sum, kind) => sum + KPI_TEXT_BLOCK_HEIGHT[kind] + KPI_ROW_PADDING, 0) +
  Math.max(0, kinds.length - 1) * KPI_ROW_DIVIDER;

/**
 * Chart height for the Расход chart. The Расход column is a single side-by-side [text | chart]
 * row that fills the whole right column (align:stretch with the KPI column), so the chart fills
 * the column minus its own row padding. Falls back to the ratio height when there are no KPI rows.
 */
export const spendChartHeight = (kpiKinds: MetricKind[]): number => {
  if (kpiKinds.length === 0) {
    return kpiChartHeight('ratio');
  }
  return kpiColumnHeight(kpiKinds) - KPI_ROW_PADDING;
};
